import appService from '@/modules/app/services/appService';
import embeddedAppStore from '@/modules/app/store/embeddedAppStore';
// Only these apps will use the angular implementation of embedded apps
import ANGULAR_EMBEDDED_APPS from './angularEmbeddedApps';

const state = {
  billingApp: undefined,
  availableMarketApps: [],
  externalApplications: [],
};

/**
 * Mark specific apps that were migrated to POV
 * @param {Object} apps - Object containing app data
 * @param {Bool} forceMigrated - Force marking as migrated app
 * @returns {Array} Transformed array of apps
 */
const markMigratedApps = (apps, forceMigrated) => [...Object.values(apps).map((item) => {
  if (forceMigrated || (item.app_code_name && !ANGULAR_EMBEDDED_APPS.includes(item.app_code_name))) {
    return {
      ...item,
      migrated: true,
    };
  }
  return item;
})];

/**
 * Apply special handling for specific apps (like PayPal logo fix)
 * @param {Array} apps - Array of apps
 * @returns {Array} Apps with special handling applied
 */
const applySpecialAppHandling = (apps) => {
  const updatedApps = [...apps];
  const paypalIndex = updatedApps.findIndex((app) => app.app_code_name === 'paypalwallet');
  if (paypalIndex !== -1) {
    updatedApps[paypalIndex].description.logo = 'https://app0.vcdnita.com/vue/img/Paypal_venmo_full_sized_with_fixed_O.bd4ebfdd.svg';
  }
  return updatedApps;
};

const mutations = {
  // TODO: is there a duplication of data here? can we normalize it so that a billing app is just a getter?
  setBillingApp(state, app) {
    state.billingApp = app;
  },
  setAvailableMarketApps(state, apps) {
    state.availableMarketApps = apps;
  },
  setExternalApplications(state, apps) {
    state.externalApplications = apps;
  },
};

const actions = {
  async loadBillingAppData({ commit, state }, businessUid) {
    if (!state.billingApp && !state.billingApp?.uid) {
      const result = await appService.getAppsByType({ type: 'billing_app', businessUid });
      if (result.code !== 'error') {
        commit('setBillingApp', result.apps[0]);
      }
    }
  },
  async getAvailableMarketApps({ commit, state, rootGetters, dispatch }, params = {}) {
    const { force = false, hasPaidApps = false } = params;

    if (!state.availableMarketApps.length || force) {
      try {
        const result = await appService.getAvailableMarketApps();
        const apps = applySpecialAppHandling([...result.apps]);

        let aggregatedApps = apps;
        if (hasPaidApps) {
          const geoData = rootGetters['AccountStore/geoData'];
          aggregatedApps = await dispatch('SubscriptionStore/aggregateAppData', { apps, geoData }, { root: true });
        }

        const filteredApps = markMigratedApps(aggregatedApps, rootGetters['BusinessStore/hasFeature']('force_embedded_apps_in_POV'));
        commit('setAvailableMarketApps', filteredApps);
      } catch (error) {
        console.error('Failed to call getAvailableMarketApps', error);
      }
    }
  },
  async getExternalApplications({ commit, state, rootGetters }, force = false) {
    if (!state.externalApplications.length || force) {
      try {
        const result = await appService.getExternalApps();
        const filteredApps = markMigratedApps(result.apps, rootGetters['BusinessStore/hasFeature']('force_embedded_apps_in_POV'));
        commit('setExternalApplications', filteredApps);
      } catch (error) {
        console.error('Failed to call getExternalApps', error);
      }
    }
  },
};

const getters = {
  billingApp: (state) => state.billingApp,
  availableMarketApps: (state) => state.availableMarketApps,
  getMarketAppById: (state) => (id) => state.availableMarketApps.find((app) => app.app_code_name === id),
  externalApplications: (state) => state.externalApplications,
  getExternalAppById: (state) => (id) => state.externalApplications.find((app) => app.app_code_name === id),
  purchasedApps: (state) => state.availableMarketApps.filter((app) => app.paid_data && !app.paid_data.requires_purchase),
};

const modules = {
  EmbeddedAppStore: embeddedAppStore,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
  modules,
};
